import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import calendarService from '../services/calendarService';

const CalendarContext = createContext(null);

export const useCalendarContext = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendarContext must be used within a CalendarProvider');
  }
  return context;
};

export const CalendarProvider = ({ children, currentUser }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('month'); // month, week, day
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationIntervalRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const shownNotificationsRef = useRef(new Set());
  const lastSyncRef = useRef(null);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  const loadEvents = useCallback(async (date = currentDate, silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      // Get start and end of the month (with padding for calendar view)
      const year = date.getFullYear();
      const month = date.getMonth();
      const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 2, 0).toISOString().split('T')[0];

      const data = await calendarService.getEvents(startDate, endDate, currentUser?.id);
      setEvents(data.events || []);
      lastSyncRef.current = new Date();
    } catch (err) {
      if (!silent) {
        setError('Failed to load calendar events');
      }
      console.error(err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [currentDate, currentUser]);

  // Refresh events from SuiteCRM (silent background refresh)
  const refreshEvents = useCallback(async () => {
    if (isPanelOpen && currentUser?.id) {
      await loadEvents(currentDate, true);
    }
  }, [isPanelOpen, currentUser, currentDate, loadEvents]);

  const createEvent = useCallback(async (eventData) => {
    try {
      const data = await calendarService.createEvent({
        ...eventData,
        user_id: currentUser?.id
      });
      if (data.success) {
        await loadEvents();
        return data;
      }
      throw new Error(data.message);
    } catch (err) {
      console.error('Failed to create event:', err);
      throw err;
    }
  }, [currentUser, loadEvents]);

  const updateEvent = useCallback(async (eventData) => {
    try {
      const data = await calendarService.updateEvent(eventData);
      if (data.success) {
        await loadEvents();
        return data;
      }
      throw new Error(data.message);
    } catch (err) {
      console.error('Failed to update event:', err);
      throw err;
    }
  }, [loadEvents]);

  const deleteEvent = useCallback(async (id, type) => {
    try {
      const data = await calendarService.deleteEvent(id, type);
      if (data.success) {
        await loadEvents();
        return data;
      }
      throw new Error(data.message);
    } catch (err) {
      console.error('Failed to delete event:', err);
      throw err;
    }
  }, [loadEvents]);

  const navigateMonth = useCallback((direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  }, []);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const openModal = useCallback((event = null, date = null) => {
    if (event) {
      setSelectedEvent(event);
    } else {
      // Creating new event — use the clicked LOCAL date/hour (no UTC shift).
      const p = (n) => String(n).padStart(2, '0');
      const localDate = (d) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
      const hr = date && date.getHours() ? date.getHours() : 9;
      setSelectedEvent({
        title: '',
        description: '',
        type: 'meeting',
        start: date ? `${localDate(date)}T${p(hr)}:00` : '',
        end: date ? `${localDate(date)}T${p((hr + 1) % 24)}:00` : '',
        location: '',
        status: 'Planned'
      });
    }
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { ...notification, id }]);
    // Auto-remove after 10 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 10000);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Check for upcoming events and show notifications
  const checkUpcomingEvents = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      const upcomingEvents = await calendarService.getUpcomingEvents(currentUser.id, 15);

      upcomingEvents.forEach(event => {
        // Only show notification once per event
        if (!shownNotificationsRef.current.has(event.id)) {
          shownNotificationsRef.current.add(event.id);

          const eventStart = new Date(event.start);
          const now = new Date();
          const minutesUntil = Math.round((eventStart - now) / 60000);

          addNotification({
            type: event.type,
            title: `Upcoming ${event.type}`,
            message: `"${event.title}" starts in ${minutesUntil} minute${minutesUntil !== 1 ? 's' : ''}`
          });
        }
      });
    } catch (err) {
      console.error('Error checking upcoming events:', err);
    }
  }, [currentUser, addNotification]);

  // Load events when panel opens or date changes
  useEffect(() => {
    if (isPanelOpen) {
      loadEvents();
    }
  }, [isPanelOpen, currentDate, loadEvents]);

  // Auto-sync with SuiteCRM every 30 seconds when panel is open
  useEffect(() => {
    if (isPanelOpen && currentUser?.id) {
      // Start sync polling
      syncIntervalRef.current = setInterval(refreshEvents, 30000);

      return () => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
        }
      };
    }
  }, [isPanelOpen, currentUser, refreshEvents]);

  // Start notification checking when user is logged in
  useEffect(() => {
    if (currentUser?.id) {
      // Check immediately
      checkUpcomingEvents();

      // Check every minute
      notificationIntervalRef.current = setInterval(checkUpcomingEvents, 60000);

      return () => {
        if (notificationIntervalRef.current) {
          clearInterval(notificationIntervalRef.current);
        }
      };
    }
  }, [currentUser, checkUpcomingEvents]);

  const value = {
    isPanelOpen,
    currentDate,
    events,
    loading,
    error,
    view,
    selectedEvent,
    isModalOpen,
    notifications,
    currentUser,
    openPanel,
    closePanel,
    togglePanel,
    loadEvents,
    refreshEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    navigateMonth,
    goToToday,
    setView,
    openModal,
    closeModal,
    addNotification,
    removeNotification,
  };

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};

export default CalendarContext;
